import { MILISECONDS_IN_DAY, UNKNOWN_DATUM } from 'business/constants';
import {
    DataTypes as DT,
    TimeScales as TS,
    SlidingTimeScales as STS,
    OrderedColumnsT,
    ExportFileT,
    ChartSettingsT,
    canCumulateDataTypes,
    VizType,
} from 'business/definitions';
import { camelize } from 'helpers/syntaxHelper';
import uniq from 'lodash/uniq';
import {
    capitalizeAndKeepTheRest,
    isDate,
    isTypeNumeric,
    orderColumns,
} from 'helpers/helpers';
import {
    formatData,
    formatDisplay,
    formatSlidingWeek,
    formatSlidingYear,
} from 'helpers/formatHelper';
import { queryToSearch } from 'routes/Explore/components/Sentence/helpers';
import { translateBlock } from 'i18n';
import {
    EffectiveRequestFilter,
    GetDataMakerResponse,
} from 'components/ChartContainer/types';
import {
    getYearlyComparisonCategoryScale,
    isYearlyComparisonScale,
} from './yearlyComparison/scale';
import { MONTHS, QUARTERS } from './dateHelper';
import { canCumulateVizTypes } from './vizInfos';
import { trackEvent } from './trackerHelpers';

export const GEO_TYPES = ['coordinates', 'polygon'];

/**
 * Format the data returned by a query for D3
 * @param  {Array} data    data returned by the get_data endpoint
 * @param  {Array} columns columns returned by the get_data endpoint
 * @return {formattedData}         formatted data ready to be used by D3
 */
export const cleanChartData = (
    data: ResponseDataT[],
    columns: ResponseColumnT[],
): ResponseDataT[] => {
    const formattedData: ResponseDataT[] = data.map((d: ResponseDataT) => {
        const newD = {};
        columns.forEach((col) => {
            const formattedValue = formatData(d[col.key], col.type);
            if (
                (col.type === 'integer' || col.type === 'float') &&
                Number.isNaN(formattedValue)
            ) {
                // @ts-expect-error [TS migration] Was not detected by flow
                newD[col.key] = UNKNOWN_DATUM;
            } else {
                // @ts-expect-error [TS migration] Was not detected by flow
                newD[col.key] = formattedValue;
            }
        });
        return newD;
    });
    return formattedData;
};

function isDiscrete(type: DataTypesT): boolean {
    return [DT.category, DT.integer, DT.text, DT.year].includes(type);
}

export function isContinuous(type: DataTypesT): boolean {
    return !isDiscrete(type);
}

export function isDateOrTime(type: DataTypesT): boolean {
    return isDate(type) || type === 'time';
}

export const getColumnType = (
    columns: ResponseColumnT[],
): { [key: string]: number } => {
    const response = {
        integer: 0,
        float: 0,
        date: 0,
        time: 0,
        money: 0,
        category: 0,
        text: 0,
        coordinates: 0,
        polygon: 0,
        continuous: 0,
        discrete: 0,
        geo: 0,
    };
    columns.forEach((col) => {
        if (col.type === 'fraction') {
            response.float += 1;
        } else if (col.type === 'datetime') {
            // datetime is treated the same way as date for the smart frontend
            response.date += 1;
        } else {
            // @ts-expect-error [TS migration] Was not detected by flow
            response[col.type] += 1;
        }

        if (isDiscrete(col.type)) {
            response.discrete += 1;
        } else {
            response.continuous += 1;
        }
        if (GEO_TYPES.includes(col.type)) {
            response.geo += 1;
        }
    });
    return response;
};

/**
 * Get the columns from the backend along with the data and set the columns order.
 * The metric is also included, since if the user asks for a list, it will be discrete.
 * The rule are detailed in the function. The goal is to make the chart as readable as possible.
 * This is the heart of the magic frontend.
 */
export function getOrderedColumns(
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    vizType: string,
    reverseGroupBy?: boolean,
    isYearlyComparison?: boolean | null,
): OrderedColumnsT {
    let effectiveColumns: ResponseColumnT[] = [];
    const emptyColumns: ResponseColumnT[] = [];

    columns.forEach((col) => {
        const values = [...new Set(data.map((d) => d[col.key]))];
        if (values.length === 1 && !col.isMetric) {
            emptyColumns.push({ ...col });
        } else {
            // Non empty column

            effectiveColumns.push({
                ...col,
                valuesCount: values.length,
            });
        }
    });

    const sortFunction = (a: ResponseColumnT, b: ResponseColumnT) => {
        const aValue = a.valuesCount ?? 0;
        const bValue = b.valuesCount ?? 0;
        return reverseGroupBy
            ? aValue < bValue
                ? 1
                : -1
            : aValue < bValue
              ? -1
              : 1;
    };

    effectiveColumns = effectiveColumns.sort(sortFunction);

    const yearlyComparisonScale = getYearlyComparisonCategoryScale(columns);
    if (isYearlyComparison && yearlyComparisonScale) {
        // When always want the yearly comparison scale values to be display on the x axis
        // thus when sort the effective column to put them first
        effectiveColumns = effectiveColumns.sort((a) =>
            a.key === yearlyComparisonScale ? 0 : 1,
        );
    }
    // Some charts can loose their columns when we use a filter on dashboards.
    // For charts with an axis, the xColumn can be hidden after a filter has
    // been selected.
    // This is quite ugly, but we have no other way of knowing if
    // it is the dashboard or the explore container asking.
    // The explore container doesn't pass the vizType, so the condition will never be true.
    if (
        emptyColumns.length > 0 &&
        [
            'pie',
            'donut',
            'bar',
            'horizontal_bar',
            'polar_bar',
            'pyramid',
            'line',
            'scatterplot',
            'map',
        ].includes(vizType)
    ) {
        if (effectiveColumns.length === 0 && emptyColumns.length === 2) {
            effectiveColumns = [...emptyColumns];
        } else if (effectiveColumns.length <= 2) {
            // NB: The case where effectiveColumns == 2 and where adding an emptyColumns is still
            // useful is to get the drill down with breadcrumbs logic working when there is only
            // one service visible but we still want to be able to see the service's children
            // see https://github.com/mantic-data/vision-front/issues/2940
            let extraColumn;
            if (
                [
                    'pie',
                    'donut',
                    'bar',
                    'horizontal_bar',
                    'polar_bar',
                    'pyramid',
                    'line',
                    'scatterplot',
                ].includes(vizType)
            ) {
                if (isYearlyComparison) {
                    // We only keep the month or day ar quarter value
                    extraColumn = emptyColumns.find((col) =>
                        isYearlyComparisonScale(col.key),
                    );
                } else {
                    // An effective column was removed by a dashboard filter.
                    // We try to find it and re-add it to the array.
                    // We arbitrarly we look for a date column, then a number and then we simply take the first column.
                    // TODO: Look at the filter to know which column should be added.
                    extraColumn = emptyColumns.find((col) =>
                        isDateOrTime(col.type),
                    );
                    if (!extraColumn) {
                        extraColumn = emptyColumns.find((col) =>
                            [DT.float, DT.money, DT.integer].includes(col.type),
                        );
                    }
                }
            } else if (vizType === 'map') {
                // For a map, we need the coordinates
                extraColumn = emptyColumns.find((col) =>
                    [DT.polygon, DT.coordinates].includes(col.type),
                );
            }

            if (!extraColumn) {
                extraColumn = emptyColumns[0];
            }
            effectiveColumns.push(extraColumn);
        }
    }

    if (effectiveColumns.length === 0 && emptyColumns.length > 0) {
        // Means we have a single point in a 1D space
        const emptyColumnsType = getColumnType(emptyColumns);
        if (
            emptyColumnsType.integer +
                emptyColumnsType.money +
                emptyColumnsType.float ===
            1
        ) {
            const column = emptyColumns.find((col) =>
                ['float', 'money', 'integer', 'fraction'].includes(col.type),
            );
            // We have a single number columns that we need to find
            if (column) {
                return {
                    y: column,
                };
            }
        }
    }

    /* We fill responseColumns depending on the effectiveColumns.
    By default, y is the first effective column, x the second one and z
    the third */
    const responseColumns = {};

    ['y', 'x', 'z'].forEach((value, index) => {
        if (effectiveColumns[index]) {
            // @ts-expect-error [TS migration] Was not detected by flow
            responseColumns[value] = effectiveColumns[index];
        }
    });

    const columnTypes = getColumnType(effectiveColumns);

    const locationPathname = window.location.pathname;
    const isInExplore = locationPathname.startsWith('/explore');
    const match = locationPathname.match(/\/explore\/(\d+)/);
    const exploreChartId = match ? match[1] : null;

    const metricsNumber = effectiveColumns.filter((col) => col.isMetric).length;

    const getTrackerMetadata = (id: string) => ({
        id,
        effectiveColumns: effectiveColumns.length,
        metricsNumber,
        ...columnTypes,
        vizType,
        reverseGroupBy,
        isInExplore,
        exploreChartId,
    });

    switch (effectiveColumns.length) {
        case 0:
            // No effective column, there is only one point of value.
            trackEvent(
                'decision/magic-frontend',
                getTrackerMetadata('no-effective-column'),
            );
            break;
        case 1:
            // One effective column, a list of values
            // @ts-expect-error [TS migration] Was not detected by flow
            responseColumns.y = effectiveColumns[0];
            trackEvent(
                'decision/magic-frontend',
                getTrackerMetadata('single-effective-column'),
            );
            break;
        case 2:
            // Two effective columns, we can start plotting things
            switch (columnTypes.continuous) {
                case 0:
                    /* Two discrete variables.
                    If there is an int and a category the integer will serve as the yAxis,
                    otherwise we put the one with the highest number of values as y (order 0),
                    and the other one as xAxis (order 1). It is not clear if the chart will be more readable. */
                    if (columnTypes.integer === 1) {
                        // There is one integer, this will be the x
                        const integerIndex = effectiveColumns.findIndex(
                            (col) => col.type === 'integer',
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[integerIndex];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[1 - integerIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/no-continuous/one-integer',
                            ),
                        );
                    } else if (metricsNumber === 1) {
                        // We have a metric integer and a column integer, we put the metric as y
                        const metricIndex = effectiveColumns.findIndex(
                            (col) => col.isMetric,
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[metricIndex];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[1 - metricIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/no-continuous/one-integer-metric-and-dimension',
                            ),
                        );
                    }
                    break;
                case 1: {
                    // One continuous and one discrete variable
                    // The categorical variable will be the xAxis, the other one will be y.
                    const continuousIndex = effectiveColumns.findIndex((col) =>
                        isContinuous(col.type),
                    );
                    if (
                        columnTypes.date === 1 ||
                        columnTypes.time === 1 ||
                        columnTypes.geo === 1
                    ) {
                        // If it is a date or a location we use it on the xAxis
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y =
                            effectiveColumns[1 - continuousIndex];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[continuousIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/one-continuous/date-or-time-or-geo',
                            ),
                        );
                    } else {
                        // We add another case to the magic frontend to handle this bug https://app.shortcut.com/mantic/story/31453/requ%C3%AAte-le-nombre-de-group%C3%A9-par-%C3%A2ge-trace-le-graphique-dans-le-mauvais-sens-et-on-ne-peut-pas-utiliser-la-conception and we hope for a fast arrival of the EPIC deleting the magic frontend
                        if (metricsNumber === 1) {
                            const metricIndex = effectiveColumns.findIndex(
                                (col) => col.isMetric,
                            );
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.y = effectiveColumns[metricIndex];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.x =
                                effectiveColumns[1 - metricIndex];
                        } else {
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.y =
                                effectiveColumns[continuousIndex];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.x =
                                effectiveColumns[1 - continuousIndex];
                        }
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/one-continuous/other',
                            ),
                        );
                    }
                    break;
                }
                case 2: {
                    // Two continuous variables
                    if (
                        columnTypes.date === 1 ||
                        columnTypes.time === 1 ||
                        columnTypes.geo === 1
                    ) {
                        // There is one date or a location, this will be the x
                        const dateIndex = effectiveColumns.findIndex(
                            (col) =>
                                isDate(col.type) ||
                                col.type === 'time' ||
                                GEO_TYPES.includes(col.type),
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[1 - dateIndex];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[dateIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/two-continuous/one-date-or-time-or-geo',
                            ),
                        );
                    } else if (metricsNumber === 1) {
                        // We have a metric and a column, we put the metric as y
                        const metricIndex = effectiveColumns.findIndex(
                            (col) => col.isMetric,
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[metricIndex];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[1 - metricIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/two-continuous/one-metric',
                            ),
                        );
                    }
                    // Otherwise we keep the default order
                    break;
                }
                default:
                    trackEvent(
                        'decision/magic-frontend',
                        getTrackerMetadata(
                            'two-effective-columns/two-continuous/default',
                        ),
                    );
            }
            break;
        case 3:
            /* Three effective columns */
            switch (columnTypes.continuous) {
                case 0:
                    /* Only discrete variables.
                        If there are three integers or three categories, we keep the default order. */
                    if (columnTypes.integer === 1) {
                        /* The integer will become y for the treemap */
                        const integerIndex = effectiveColumns.findIndex(
                            (col) => col.type === 'integer',
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[integerIndex];
                        const remainingDimensions = [...effectiveColumns]
                            .filter((_, i) => i !== integerIndex)
                            .sort((a, b) => {
                                if (
                                    !isYearlyComparison &&
                                    a.valuesCount &&
                                    b.valuesCount
                                ) {
                                    return a.valuesCount < b.valuesCount
                                        ? 1
                                        : -1;
                                }
                                return 0;
                            });

                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = remainingDimensions[0];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.z = remainingDimensions[1];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'three-effective-columns/no-continuous/one-integer',
                            ),
                        );
                    } else if (columnTypes.integer === 2) {
                        // The integers will become x and y, the category will be z
                        let firstIntegerIndex: number;
                        if (metricsNumber === 1) {
                            // We have a metric integer and a column integer, we put the metric as y
                            firstIntegerIndex = effectiveColumns.findIndex(
                                (col) => col.isMetric,
                            );
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'three-effective-columns/no-continuous/two-integers/one-metric',
                                ),
                            );
                        } else {
                            firstIntegerIndex = effectiveColumns.findIndex(
                                (col) => col.type === 'integer',
                            );
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'three-effective-columns/no-continuous/two-integers/multiple-metrics',
                                ),
                            );
                        }
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[firstIntegerIndex];
                        const secondIntegerIndex = effectiveColumns.findIndex(
                            (col, i) =>
                                col.type === 'integer' &&
                                i !== firstIntegerIndex,
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x =
                            effectiveColumns[secondIntegerIndex];

                        const remainingIndex = [0, 1, 2].filter(
                            (i) =>
                                i !== firstIntegerIndex &&
                                i !== secondIntegerIndex,
                        )[0];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.z = effectiveColumns[remainingIndex];
                    }
                    break;
                case 1: {
                    /* One continuous variable, it will be the y or the x. The discrete value with the most values
                        will be the x, and the other one will be colors. */
                    /* If we have a date, we put is as x */
                    if (
                        columnTypes.date === 1 ||
                        columnTypes.time === 1 ||
                        columnTypes.geo === 1
                    ) {
                        const dateOrLocationIndex = effectiveColumns.findIndex(
                            (col) =>
                                isDate(col.type) ||
                                col.type === 'time' ||
                                GEO_TYPES.includes(col.type),
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x =
                            effectiveColumns[dateOrLocationIndex];
                        if (columnTypes.integer === 1) {
                            const integerIndex = effectiveColumns.findIndex(
                                (col) => col.type === 'integer',
                            );
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.y = effectiveColumns[integerIndex];
                            const remainingIndex = [0, 1, 2].filter(
                                (i) =>
                                    i !== integerIndex &&
                                    i !== dateOrLocationIndex,
                            )[0];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.z =
                                effectiveColumns[remainingIndex];
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/one-continuous/date-or-time-or-geo/one-integer',
                                ),
                            );
                        } else {
                            const remainingDimensions = [...effectiveColumns]
                                .filter((_, i) => i !== dateOrLocationIndex)
                                .sort(
                                    // @ts-expect-error [TS migration] (previously $FlowFixMe) : build different types for columns
                                    (a, b) => a.valuesCount < b.valuesCount,
                                );

                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.y = remainingDimensions[0];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.z = remainingDimensions[1];
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/one-continuous/date-or-time-or-geo/other',
                                ),
                            );
                        }
                    } else {
                        const continuousIndex = effectiveColumns.findIndex(
                            (col) => isContinuous(col.type),
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[continuousIndex];
                        if (columnTypes.integer === 1) {
                            // If there is an integer we put it as x
                            const integerIndex = effectiveColumns.findIndex(
                                (col) => col.type === 'integer',
                            );
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.x = effectiveColumns[integerIndex];
                            const remainingIndex = [0, 1, 2].filter(
                                (i) =>
                                    i !== integerIndex && i !== continuousIndex,
                            )[0];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.z =
                                effectiveColumns[remainingIndex];
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/one-continuous/other/one-integer',
                                ),
                            );
                        } else {
                            // The discrete variable with the least values will get the colors
                            const firstDiscreteIndex =
                                effectiveColumns.findIndex((col) =>
                                    isDiscrete(col.type),
                                );
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.x =
                                effectiveColumns[firstDiscreteIndex];

                            const remainingIndex = [0, 1, 2].filter(
                                (i) =>
                                    i !== firstDiscreteIndex &&
                                    i !== continuousIndex,
                            )[0];
                            // @ts-expect-error [TS migration] Was not detected by flow
                            responseColumns.z =
                                effectiveColumns[remainingIndex];
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/one-continuous/other/other',
                                ),
                            );
                        }
                    }
                    break;
                }
                case 2: {
                    /* Two continuous variables, and one categorical. If there is a date we'll use it as x, otherwise we just use the default order. */
                    const discreteIndex = effectiveColumns.findIndex((col) =>
                        isDiscrete(col.type),
                    );
                    // @ts-expect-error [TS migration] Was not detected by flow
                    responseColumns.z = effectiveColumns[discreteIndex];
                    if (
                        columnTypes.date +
                            columnTypes.time +
                            columnTypes.geo ===
                        1
                    ) {
                        // If there is a date or a coordinates it becomes the x axis
                        const dateIndex = effectiveColumns.findIndex(
                            (col) =>
                                isDate(col.type) ||
                                col.type === 'time' ||
                                GEO_TYPES.includes(col.type),
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[dateIndex];
                        const remainingIndex = [0, 1, 2].filter(
                            (i) => i !== dateIndex && i !== discreteIndex,
                        )[0];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y = effectiveColumns[remainingIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/two-continuous/one-time-date-or-geo',
                            ),
                        );
                    } else {
                        // The discrete variable with the least values will get the x axis
                        let firstContinuousIndex: number;
                        if (metricsNumber === 1) {
                            // We have a metric integer and a column integer, we put the metric as y
                            firstContinuousIndex = effectiveColumns.findIndex(
                                (col) => col.isMetric,
                            );
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/two-continuous/other/one-metric',
                                ),
                            );
                        } else {
                            firstContinuousIndex = effectiveColumns.findIndex(
                                (col) => isContinuous(col.type),
                            );
                            trackEvent(
                                'decision/magic-frontend',
                                getTrackerMetadata(
                                    'two-effective-columns/two-continuous/other/multi-metrics',
                                ),
                            );
                        }
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y =
                            effectiveColumns[firstContinuousIndex];
                        const remainingIndex = [0, 1, 2].filter(
                            (i) =>
                                i !== firstContinuousIndex &&
                                i !== discreteIndex,
                        )[0];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[remainingIndex];
                    }
                    break;
                }
                case 3: {
                    /* Three continuous variables, we just check the date and order them to
                    give colors to the smallest amount of values */
                    if (columnTypes.date === 1 || columnTypes.time === 1) {
                        // If there is a date it becomes the x axis
                        const dateIndex = effectiveColumns.findIndex(
                            (col) => isDate(col.type) || col.type === 'time',
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.x = effectiveColumns[dateIndex];
                        const firstContinuousIndex = effectiveColumns.findIndex(
                            (col) => !isDate(col.type) && col.type !== 'time',
                        );
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.y =
                            effectiveColumns[firstContinuousIndex];
                        const remainingIndex = [0, 1, 2].filter(
                            (i) =>
                                i !== firstContinuousIndex && i !== dateIndex,
                        )[0];
                        // @ts-expect-error [TS migration] Was not detected by flow
                        responseColumns.z = effectiveColumns[remainingIndex];
                        trackEvent(
                            'decision/magic-frontend',
                            getTrackerMetadata(
                                'two-effective-columns/three-continuous/date-or-time',
                            ),
                        );
                    }
                    trackEvent(
                        'decision/magic-frontend',
                        getTrackerMetadata(
                            'two-effective-columns/three-continuous/broken',
                        ),
                    );
                    // else we don't try to do anything
                    break;
                }
                default:
            }
            break;
        default:
            // 4 or more columns. This only happens in formatTableData, we simply need the x value
            // TODO: Separate this function so that the xColumn can be determined independently from
            // the rest.
            if (columnTypes.date > 0) {
                // @ts-expect-error [TS migration] Was not detected by flow
                responseColumns.x = effectiveColumns.find((col) =>
                    [DT.date, DT.time].includes(col.type),
                );
                trackEvent(
                    'decision/magic-frontend',
                    getTrackerMetadata('four-or-more-effective-columns/dates'),
                );
            } else if (columnTypes.integer > 0) {
                // @ts-expect-error [TS migration] Was not detected by flow
                responseColumns.x = effectiveColumns.find(
                    (col) => col.type === DT.integer,
                );
                trackEvent(
                    'decision/magic-frontend',
                    getTrackerMetadata(
                        'four-or-more-effective-columns/integers',
                    ),
                );
            } else if (columnTypes.category + columnTypes.text > 0) {
                // @ts-expect-error [TS migration] Was not detected by flow
                responseColumns.x = effectiveColumns.find((col) =>
                    [DT.category, DT.text].includes(col.type),
                );
                trackEvent(
                    'decision/magic-frontend',
                    getTrackerMetadata(
                        'four-or-more-effective-columns/categories',
                    ),
                );
            }
    }

    if (
        ['pie', 'donut'].includes(vizType) &&
        // @ts-expect-error [TS migration] Was not detected by flow
        !responseColumns.x &&
        emptyColumns.length === 1
    ) {
        // One of the column got deleted by a filter in the dashboard.
        // Since there is only one empty column, it has to be this one.
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.x = emptyColumns[0];
        trackEvent(
            'decision/magic-frontend',
            getTrackerMetadata('round-chart-patch'),
        );
    }
    // Special hack for treemap chart.
    // We fill both x and z columns when data length is of one, because it leads to a single effective column.
    if (
        vizType === 'treemap' &&
        // @ts-expect-error [TS migration] Was not detected by flow
        !responseColumns.x &&
        // @ts-expect-error [TS migration] Was not detected by flow
        !responseColumns.z &&
        emptyColumns.length >= 2
    ) {
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.x = emptyColumns[0];
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.z = emptyColumns[1];
        trackEvent(
            'decision/magic-frontend',
            getTrackerMetadata('single-effective-column-treemap-chart-patch'),
        );
    }
    if (
        ['area', 'treemap'].includes(vizType) &&
        // @ts-expect-error [TS migration] Was not detected by flow
        !responseColumns.z &&
        emptyColumns.length === 1
    ) {
        // One of the column got deleted by a filter in the dashboard.
        // Since there is only one empty column, it has to be this one.
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.z = emptyColumns[0];
        trackEvent(
            'decision/magic-frontend',
            getTrackerMetadata('area-treemap-chart-patch'),
        );
    }
    // TODO: Remove this hack
    if (
        // @ts-expect-error [TS migration] Was not detected by flow
        (responseColumns.x && responseColumns.x.key === 'generatedCategory') ||
        // @ts-expect-error [TS migration] Was not detected by flow
        (responseColumns.z && responseColumns.z.key === 'month') ||
        // @ts-expect-error [TS migration] Was not detected by flow
        (responseColumns.z && GEO_TYPES.includes(responseColumns.z.type))
    ) {
        // Month should be in x, but it sometimes goes in z if there are more values
        // for another column. We invert x and z to correct it.
        // Also, generatedCategory should be in z. Both cases cannot happen at the same time.
        // @ts-expect-error [TS migration] Was not detected by flow
        if (responseColumns.x && responseColumns.z) {
            // Checking that it will not result in emptying one column
            // @ts-expect-error [TS migration] Was not detected by flow
            const xDimension = { ...responseColumns.x };
            // @ts-expect-error [TS migration] Was not detected by flow
            const zDimension = { ...responseColumns.z };
            // @ts-expect-error [TS migration] Was not detected by flow
            responseColumns.x = zDimension;
            // @ts-expect-error [TS migration] Was not detected by flow
            responseColumns.z = xDimension;
            trackEvent(
                'decision/magic-frontend',
                getTrackerMetadata('idk-what-happened'),
            );
        }
    }

    // Special hack for age pyramid. We want y as an integer (number of agents)
    // and x as a float (age)
    if (
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.x &&
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.x.key === 'agent' &&
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.y &&
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.y.key === 'agent.age'
    ) {
        // @ts-expect-error [TS migration] Was not detected by flow
        const xDimension = { ...responseColumns.x };
        // @ts-expect-error [TS migration] Was not detected by flow
        const yDimension = { ...responseColumns.y };
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.x = yDimension;
        // @ts-expect-error [TS migration] Was not detected by flow
        responseColumns.y = xDimension;
        trackEvent(
            'decision/magic-frontend',
            getTrackerMetadata('pyramid-chart-patch'),
        );
    }
    // @ts-expect-error [TS migration] (previously $FlowFixMe) flow exact type migration: type issue
    return responseColumns;
}

/**
 * Get a query string from a serialized query
 */
export const getQueryString = (
    query: QueryT,
    downloadFormat?: ExportFileT | null,
): string => {
    let search = queryToSearch(query);
    if (downloadFormat) {
        search += `&download_format=${downloadFormat}`;
    }
    return search;
};

export const translateTreeLabel = (
    title: string,
    treeValueTranslations?: { [key: string]: string } | null,
): string | null => {
    if (title === undefined) return null;
    if (title === UNKNOWN_DATUM) return '-';
    return treeValueTranslations
        ? treeValueTranslations[title.toString()]
        : null;
};

/**
 * Formats a date value based on the provided time scale.
 *
 * @param value - The date value to format.
 * @param type - The type of data.
 * @param scale - The time scale to use for formatting.
 * @param interval - interval for range-based formatting, used for week scale.
 * @param isAxis - Whether the function is used for an axis label.
 * @returns - The formatted date string.
 */
const formatDateByScale = (
    value: string | number,
    type: DataTypesT,
    scale: TimeScalesT,
    interval?: number | null,
    isAxis: boolean = false,
): string => {
    const date = new Date(parseInt(value.toString(), 10));
    const year = date.getFullYear();

    switch (scale) {
        case TS.week: {
            if (typeof value !== 'number' || !interval)
                return formatDisplay(value, type);
            const startDate = formatDisplay(value, type);
            const endDate = formatDisplay(value + interval, type);
            return `${startDate} - ${endDate}`;
        }
        case TS.month:
            return `${MONTHS[date.getMonth()]} ${year}`;
        case TS.year:
            // we only want to display the year once per year - not once per tick !
            if (isAxis && date.getMonth() !== 0) return '';
            return `${year}`;
        case TS.quarter:
            return `${QUARTERS[date.getMonth()]} ${year}`;
        case TS.schoolYear:
            return `${year} - ${year + 1}`;
        case STS.slidingWeek:
            return formatSlidingWeek(date);
        case STS.slidingYear:
            return formatSlidingYear(date);
        default:
            return formatDisplay(value, type);
    }
};

/**
 * Format label of an eChart axis
 */
export function formatAxisLabel(
    value: string | number | undefined,
    type: DataTypesT,
    decimalPrecision: 0 | 1 | 2,
    displayBy: ChartSettingsT['displayBy'],
    interval?: number | null,
    scale?: TimeScalesT | null,
    areTicksRotated?: boolean | null,
    treeValueTranslations?: { [key: string]: string } | null,
    isPyramid: boolean = false,
): string | number {
    if (value === undefined) return '';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isPyramid) {
        // We want to display the full value interval for the pyramid chart
        // e.g. : we want to display 20-24 instead of 22.5
        // N.B. : this will need to be update if the bin size is updated
        return `${numericValue - 2.5} - ${numericValue + 1.5}`;
    }
    if (isDate(type) && scale) {
        return formatDateByScale(value, type, scale, interval, true);
    }
    if (isTypeNumeric(type) && interval) {
        return `${numericValue - interval / 2} - ${numericValue + interval / 2}`;
    }
    const label = formatDisplay(value, type, decimalPrecision, displayBy);
    const translatedLabel = translateTreeLabel(
        value.toString(),
        treeValueTranslations,
    );
    const labelToDisplay = translatedLabel || label;
    if (label.length > 15) {
        // We only display the first 15 chars for readability
        if (areTicksRotated) return `${labelToDisplay.substring(0, 15)}...`;
    }
    return capitalizeAndKeepTheRest(labelToDisplay);
}

/**
 * Format tooltip title of a chart, useful for histogram and date ranges.
 */
export function formatTooltipTitle(
    value: string | number,
    type: DataTypesT,
    scale?: TimeScalesT | null,
    interval?: number | null,
    treeValueTranslations?: { [key: string]: string } | null,
): string | number {
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isDate(type) && scale && interval) {
        return formatDateByScale(value, type, scale, interval);
    } else if (type === DT.time) {
        return `${formatDisplay(value, type)} - ${formatDisplay(
            numericValue + (interval ?? 0),
            type,
        )}`;
    } else if ([DT.float, DT.money].includes(type) && interval) {
        // Used for histograms
        return `${numericValue - interval / 2} - ${numericValue + interval / 2}`;
    }
    const formatedTitle = formatDisplay(value, type, 0);
    const translatedTitle = translateTreeLabel(
        value.toString(),
        treeValueTranslations,
    );
    return translatedTitle || formatedTitle;
}

/**
 * Build a full tooltip string for an eChart graph.
 * Tooltips cannot be HTML with eCharts, so we simply build
 * a string containing all the required elements.
 */
export function getTooltipString(
    title: string,
    series: Array<{ label: string; value: string | number; marker: string }>,
) {
    const seriesString = series.map((serie) =>
        serie.value != null
            ? `<div class="tooltip-serie">${serie.marker}${capitalizeAndKeepTheRest(serie.label)} : ${serie.value}</div>`
            : null,
    );
    return `
        <div>${capitalizeAndKeepTheRest(title)} ${seriesString.join(' ')}</div>
    `;
}

export function getAxisType(type: DataTypesT): 'category' | 'time' | 'value' {
    if (isDiscrete(type)) {
        return 'category';
    }
    if (isDate(type) || type === DT.time) {
        return 'time';
    }
    return 'value';
}

export function getTimeInterval(
    scale?: TimeScalesT | null,
    date: Date | null = null,
): number {
    // FIXME: should be dead code - remove me
    switch (scale) {
        case 'day':
            return MILISECONDS_IN_DAY;
        case 'week':
            return MILISECONDS_IN_DAY * 7;
        case 'month': {
            const days = date
                ? new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                : 30;
            return MILISECONDS_IN_DAY * days;
        }
        case 'quarter':
            return MILISECONDS_IN_DAY * 31 * 3;
        case 'year':
            return MILISECONDS_IN_DAY * 365;
        case 'school_year':
            return MILISECONDS_IN_DAY * 365;
        case 'sliding_year':
            return MILISECONDS_IN_DAY * 365;
        case 'sliding_week':
            return MILISECONDS_IN_DAY * 7;
        default:
            return 0;
    }
}

export function isEmptyChart(chartData: ResponseDataT[]): boolean {
    const onlyUnknownData = chartData.every((d) => {
        const valueSet = uniq(Object.values(d));
        return valueSet.length === 1 && valueSet[0] === UNKNOWN_DATUM;
    });
    return chartData.length === 0 || onlyUnknownData;
}

export function canCumulate(
    dimensions: ReadonlyArray<(QueryDimensionT & WithType) | ResponseColumnT>,
    vizType: VizType,
): boolean {
    return (
        dimensions.filter(
            (d) => d.type && canCumulateDataTypes.includes(d.type),
        ).length === 1 && canCumulateVizTypes.includes(vizType)
    );
}

// TODO This function is a light version of the setQueryResult we should refactor it with this last.
export function cleanQueryResult(payload: {
    queryMetrics: QueryMetricT[];
    queryDimensions: QueryDimensionT[];
    columns: ResponseColumnT[];
    data: ResponseDataT[];
    timeFilters?: EffectiveRequestFilter[];
    alertTriggered?: boolean;
}): GetDataMakerResponse {
    const { queryMetrics, queryDimensions, columns, data } = payload;

    const orderedColumns = orderColumns(columns, queryDimensions);

    // object.key is camelized, object.field is snake cased

    const queryResponseColumns = orderedColumns.map((col) => {
        let colType = col.type;
        if (colType === 'table') {
            const colMetric = queryMetrics.find(
                (m) => camelize(m.field) === col.key,
            );
            if (colMetric && colMetric.aggregation === 'number') {
                colType = 'integer';
            } else {
                colType = 'text';
            }
        }
        return {
            ...col,
            key: col.key,
            label: translateBlock(col.key),
            type: colType,
        };
    });

    const queryResponseData = cleanChartData(data, queryResponseColumns);
    return {
        columns: queryResponseColumns,
        data: queryResponseData,
        timeFilters: payload.timeFilters,
        alertTriggered: payload.alertTriggered,
    };
}
